import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Link as MuiLink,
  Stack,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { makeStyles } from 'tss-react/mui';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, Link } from 'react-router-dom';
import 'cordova-plugin-purchase';
import { useMutation } from '@tanstack/react-query';
import { attemptAsync } from 'es-toolkit/util';
import {
  useIapState,
  MEMBERSHIP_DESCRIPTION_TRANS_KEYS,
  MEMBERSHIP_TITLE_TRANS_KEYS,
} from 'features/payments/hooks/useIapState';
import { useAuthState } from 'state/useAuthState';
import {
  recurlyOnErrorHandler,
  parseRecurlyErrorToTransKey,
  processIapPurchaseApi,
  upgradeMembershipApi,
} from 'features/payments/payments.utils';
import {
  isVirtualTransaction,
  isPreCheckoutSubscriptionTransaction,
  isApprovedWatchDutyTransaction,
  restoreAndFindApprovedTransactions,
  waitForApprovedTransactionsToClear,
} from 'features/payments/utils/detectIapCollision';
import { useConfirm } from 'components/ConfirmDialog';
import { useEmailValidation } from 'hooks/useEmailValidation';
import { enqueueSnackbar } from 'notistack';
import { EmailStatus } from 'shared/getEmailValidationSchema';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { usePurchaseTracking } from 'hooks/usePurchaseTracking';
import { HttpStatusCode } from 'axios';
import AppleIcon from 'assets/apple.svg?react';
import { useKeyboardOpenState } from 'hooks/useKeyboardOpenState';
import { CheckoutPriceSummary } from 'features/payments/components/CheckoutPriceSummary';
import { Loader } from 'components/Loader';
import { DATE_ONLY_FORMAT, formatDate, safeParseDate } from 'shared/dates';
import {
  FormValues,
  getValidationSchema,
  handleValidatorSuccess,
  preProcessPurchaseIapApi,
  getPriceFromStore,
  getFormattedPriceFromStore,
} from './utils';

// Long enough that Apple's sheet, which waits on the user, never trips it.
const ORDER_RESPONSE_TIMEOUT_MS = 60_000;

const useStyles = makeStyles()(() => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  checkbox: {
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 42,
  },
}));

export const MembershipIAPCheckoutContent = (): JSX.Element => {
  const {
    checkoutDetails,
    selectedProductId,
    plan,
    loading,
    setCheckoutDetails,
    setLoading,
  } = useIapState();
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { user } = useAuthState();
  const { logPurchaseEvent } = usePurchaseTracking();
  const isKeyboardOpen = useKeyboardOpenState();
  const confirm = useConfirm();

  /**
   * Start of the current checkout attempt. An approved Watch Duty
   * subscription dated before this was already owned by the Apple ID, so it's
   * a collision, not a fresh charge. Refreshed per attempt in `handlePurchase`.
   */
  const checkoutStartedAtRef = useRef(Date.now());

  /** De-dupes the collision dialog across repeated StoreKit replays. */
  const collisionDialogOpenRef = useRef(false);

  /**
   * True while the pre-order restore replays this Apple ID's history. Those
   * replays arrive as `approved` events, but `handlePurchase` judges the whole
   * set, so the listener must not act on them piecemeal.
   */
  const isRestoringIapRef = useRef(false);

  /**
   * True only between `order()` and its outcome, so a transaction only counts
   * as payment when this page asked for one. `checkoutStartedAtRef` never
   * expires, so without this a renewal of a different subscription could be
   * verified as payment for the selected plan and grant a free membership.
   */
  const orderInFlightRef = useRef(false);

  /**
   * Timer that gives up on an order which never reports back at all. Apple can
   * answer with `PaymentTransactionStateRestored`, and the plugin then drops
   * the resulting `approved` if it already sent one for the same subscription
   * in the last minute, leaving us with no event to wait for.
   */
  const orderWatchdogRef = useRef<ReturnType<typeof setTimeout>>();

  /**
   * True once this attempt's failure has been surfaced (or deliberately
   * swallowed, as with a cancellation), so one failure cannot raise two
   * snackbars when the plugin and the caller both see it.
   */
  const failureHandledRef = useRef(false);

  /**
   * Transaction ids we've flagged as collisions and finished. The `finished`
   * event fires with the transaction already in `FINISHED` state, so the
   * collision can no longer be re-derived from its fields there - we match by
   * id instead to stop the success navigation.
   */
  const collisionTransactionIdsRef = useRef<Set<string>>(new Set());

  // A lapsed member has no `planType`, so they are a new purchase, never an
  // upgrade.
  const isMembershipUpgrade = user?.supporterStatus?.planType === 'regular';
  const hasActiveMembership = Boolean(
    user?.supporterStatus?.hasActiveMembership,
  );
  const isLoggedIn = Boolean(user);

  /**
   * Whether this account has its own membership history, active or lapsed - see
   * `shared/types.ts` `UserSupporterStatus.dateActiveUntil`: present for a
   * lapsed membership too, so it is the only field that separates "subscribed
   * and lapsed" from "never subscribed".
   *
   * Derived here as a primitive rather than read off `user` inside
   * `onTransactionApproved`: that callback is a dependency of the effect
   * registering the StoreKit listeners, and that effect's cleanup cancels the
   * order timer. Depending on the `user` object would re-run it, and so cancel
   * the timer mid-order, every time `useRefreshAuthUserOnMount` refetches auth
   * into a new reference.
   */
  const hasOwnMembershipHistory =
    user?.supporterStatus?.dateActiveUntil != null;

  const formattedActiveUntil = useMemo((): string | null => {
    const dateActiveUntil = user?.supporterStatus?.dateActiveUntil;
    if (!dateActiveUntil) return null;

    const date = safeParseDate(dateActiveUntil);
    if (!date) return null;

    return formatDate(date, DATE_ONLY_FORMAT);
  }, [user?.supporterStatus?.dateActiveUntil]);

  const showMembershipEndedNotice = Boolean(
    !hasActiveMembership && !isMembershipUpgrade && formattedActiveUntil,
  );

  const emailValidationCache = useRef<Map<string, EmailStatus>>(new Map());

  const {
    control,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    getValues,
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(
      getValidationSchema(t, isMembershipUpgrade, emailValidationCache.current),
    ),
    defaultValues: {
      firstName: user?.firstName || checkoutDetails.firstName || '',
      lastName: user?.lastName || checkoutDetails.lastName || '',
      email: user?.email || checkoutDetails.email || '',
      optInToUpdates: checkoutDetails.optInToUpdates || false,
    },
    mode: 'onBlur',
  });

  const { mutate: processIapPurchase } = useMutation({
    mutationFn: processIapPurchaseApi,
  });

  const { mutate: preProcessIapPurchase } = useMutation({
    mutationFn: preProcessPurchaseIapApi,
  });

  const { mutate: upgradeMembership } = useMutation({
    mutationFn: upgradeMembershipApi,
  });

  /**
   * Finishes a stale receipt and remembers its id so `onTransactionFinished`
   * skips the success navigation. Apple requires every transaction to be
   * finished; unfinished ones are re-delivered on every launch.
   */
  const finishStaleTransaction = useCallback(
    async (transaction: CdvPurchase.Transaction): Promise<void> => {
      if (transaction.transactionId) {
        collisionTransactionIdsRef.current.add(transaction.transactionId);
      }

      try {
        await transaction.finish();
      } catch (err) {
        Sentry.captureException(err, {
          extra: { context: 'iap-collision-finish-transaction' },
        });
      }
    },
    [],
  );

  /**
   * Waits for finished receipts to leave the queue, reporting any that outlast
   * it: `order()` is accepted and answered with nothing while one lingers,
   * which otherwise shows up only as the order timer giving up.
   */
  const reportIfTransactionsRemain = useCallback(
    async (context: string): Promise<void> => {
      const remaining = await waitForApprovedTransactionsToClear();

      if (remaining) {
        Sentry.captureMessage('iap-transactions-not-cleared', {
          level: 'info',
          tags: { productId: selectedProductId, context, remaining },
        });
      }
    },
    [selectedProductId],
  );

  /**
   * Two truths, two copies: `approved-replay` for a subscription Apple
   * approved without charging, and `on-file` for a purchase that never
   * started.
   *
   * `on-file` is an App Store problem, so it routes there. A collision is an
   * account problem, so it routes to the account: `/account` to log out of the
   * wrong one, or `/login` when there is no authenticated user.
   */
  const showCollisionDialog = useCallback(
    async (
      variant: 'approved-replay' | 'on-file' = 'approved-replay',
    ): Promise<void> => {
      setLoading(false);

      if (collisionDialogOpenRef.current) {
        return;
      }

      const isOnFile = variant === 'on-file';

      const copyKey = isOnFile
        ? 'membershipCheckout.appleSubscriptionOnFile'
        : 'membershipCheckout.appleIdCollision';

      let okText: string;
      if (isOnFile) {
        okText = t('membership.subscription.manageSubscription');
      } else {
        okText = isLoggedIn
          ? t('membershipCheckout.appleIdCollision.logOut')
          : t('membershipCheckout.appleIdCollision.logIn');
      }

      collisionDialogOpenRef.current = true;
      const confirmed = await confirm(t(`${copyKey}.message`), {
        title: t(`${copyKey}.title`),
        okText,
      });
      collisionDialogOpenRef.current = false;

      if (!confirmed) {
        return;
      }

      if (isOnFile) {
        CdvPurchase.store.manageSubscriptions(
          CdvPurchase.Platform.APPLE_APPSTORE,
        );
        return;
      }

      history.push(isLoggedIn ? '/account' : '/login');
    },
    [confirm, history, isLoggedIn, setLoading, t],
  );

  const clearOrderWatchdog = useCallback((): void => {
    if (orderWatchdogRef.current) {
      clearTimeout(orderWatchdogRef.current);
      orderWatchdogRef.current = undefined;
    }
  }, []);

  const onTransactionApproved = useCallback(
    (transaction: CdvPurchase.Transaction) => {
      // 'finished' transactions also fire this event up
      if (transaction.state !== CdvPurchase.TransactionState.APPROVED) {
        return;
      }

      // On every app start on iOS, you receive a transaction with the transactionId 'appstore.application'
      // The app itself is considered a transaction. https://github.com/j3k0/cordova-plugin-purchase/issues/1428#issuecomment-1730879536
      if (isVirtualTransaction(transaction)) {
        return;
      }

      // `handlePurchase` owns the verdict on the set it restored.
      if (isRestoringIapRef.current) {
        return;
      }

      // Safety net for a replay landing after the pre-order restore: a
      // subscription the Apple ID already owns is replayed as "approved"
      // without a charge, and processing it would grant a free membership
      // (ENG-3568). Upgrades reuse an existing subscription, so skip them.
      if (
        !isMembershipUpgrade &&
        isPreCheckoutSubscriptionTransaction(
          transaction,
          checkoutStartedAtRef.current,
        )
      ) {
        // This ends the attempt either way, so stop the order timer.
        clearOrderWatchdog();

        // A StoreKit-1 restore replay never carries a purchaseDate or
        // originalTransactionId through this plugin's native bridge, so
        // product-id or date-based filtering can't distinguish "this account's
        // own stale receipt" from "a different account's collision" -
        // `hasOwnMembershipHistory` is the only signal we have that survives a
        // dateless replay.
        if (hasOwnMembershipHistory) {
          // Logged in, and this account has its own membership history: a
          // replay here is plausibly this same account's own stale/unfinished
          // receipt, not another account's collision - regardless of which
          // plan was selected. Finish it and let checkout proceed.
          Sentry.captureMessage('iap-approved-own-history-replay', {
            level: 'info',
            tags: {
              productId: selectedProductId,
              hasActiveMembership,
            },
          });
          finishStaleTransaction(transaction);
          setLoading(false);
          return;
        }

        // Logged-out (this checkout may create a new account or reuse an
        // existing one by email via processIapPurchaseApi), or logged-in with
        // no membership history at all (dateActiveUntil null - donor-only or
        // brand new): any prior approved WD transaction on this Apple ID is
        // treated as a real collision, regardless of which product/plan was
        // just selected.
        Sentry.captureMessage('iap-post-approval-rejected', {
          level: 'info',
          tags: {
            productId: selectedProductId,
            replayedProductIds:
              transaction.products?.map((p) => p.id).join(',') || 'none',
            isLoggedIn,
          },
        });

        finishStaleTransaction(transaction)
          .then(() => showCollisionDialog('approved-replay'))
          .catch((err) => {
            Sentry.captureException(err, {
              extra: { context: 'iap-collision-handler' },
            });
          });
        return;
      }

      // Nothing was ordered, so this cannot be payment for it: usually a
      // renewal after a cancelled attempt, and verifying it would grant a free
      // membership. Like the next branch, it leaves the transaction for the
      // launch sweep and keeps the order timer running, since stopping it would
      // leave the page loading forever.
      if (!orderInFlightRef.current) {
        Sentry.captureMessage('iap-approved-outside-order', {
          level: 'info',
          tags: {
            productId: selectedProductId,
            approvedProductIds:
              transaction.products?.map((p) => p.id).join(',') || 'none',
          },
        });
        return;
      }

      // Apple charges for the product ordered, so another product's
      // transaction is not this order's payment.
      const isSelectedProduct = transaction.products?.some(
        (product) => product.id === selectedProductId,
      );
      if (!isSelectedProduct) {
        Sentry.captureMessage('iap-approved-product-mismatch', {
          level: 'info',
          tags: {
            productId: selectedProductId,
            approvedProductIds:
              transaction.products?.map((p) => p.id).join(',') || 'none',
          },
        });
        return;
      }

      // This order's answer arrived, so the order timer has nothing to catch.
      clearOrderWatchdog();
      transaction.verify();
    },
    [
      finishStaleTransaction,
      showCollisionDialog,
      isMembershipUpgrade,
      hasActiveMembership,
      hasOwnMembershipHistory,
      isLoggedIn,
      selectedProductId,
      setLoading,
      clearOrderWatchdog,
    ],
  );

  const onTransactionVerified = useCallback(
    (receipt: CdvPurchase.VerifiedReceipt) => {
      clearOrderWatchdog();
      receipt.finish();
    },
    [clearOrderWatchdog],
  );

  const onTransactionFinished = useCallback(
    (transaction: CdvPurchase.Transaction) => {
      clearOrderWatchdog();
      orderInFlightRef.current = false;

      // Any `receipt.finish()` also finishes `appstore.application`, so without
      // this one purchase produced two success navigations.
      if (isVirtualTransaction(transaction)) {
        return;
      }

      // A finished collision is already FINISHED, so match by id - the
      // collision can no longer be re-derived from its fields there.
      const { transactionId } = transaction;
      if (
        transactionId &&
        collisionTransactionIdsRef.current.has(transactionId)
      ) {
        collisionTransactionIdsRef.current.delete(transactionId);
        return;
      }

      // Reset the form after processing the transaction to prevent duplicate submissions.
      // This addresses an issue where multiple calls to "processIapPurchase" can occur if
      // the same transaction is approved more than once. The duplicate calls likely stem
      // from the asynchronous behavior of the "createIapPurchase" validator function that
      // lead to potential race conditions.
      reset();

      let url = '/membership/iap_success';
      if (isMembershipUpgrade) {
        url += '?upgrade=true';
      } else if (!user) {
        url += `?clientToken=${checkoutDetails.clientToken}`;
      }
      history.push(url);
    },
    [
      clearOrderWatchdog,
      checkoutDetails.clientToken,
      history,
      isMembershipUpgrade,
      user,
      reset,
    ],
  );

  const onStoreError = useCallback(
    (err: CdvPurchase.IError) => {
      clearOrderWatchdog();
      orderInFlightRef.current = false;
      setLoading(false);
      failureHandledRef.current = true;

      // Branch on the code, not the message: StoreKit messages are localized,
      // so matching on 'cancel' gave non-English users an error snackbar and a
      // Sentry event for dismissing Apple's payment sheet.
      if (err.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
        // A cancellation is the one failure that definitively produced no
        // charge, so drop the pending-checkout marker: while it is set,
        // `useFinishStaleIapTransactions` skips its launch sweep and
        // `useReconcileInterruptedIap` keeps treating this attempt as
        // recoverable for up to 30 days. Every other error may have followed a
        // charge, so those keep the marker for recovery.
        setCheckoutDetails({ checkoutStartedAt: 0 });
        return;
      }

      enqueueSnackbar(err.message, { variant: 'error' });
      Sentry.captureMessage('iap-store-error', {
        level: 'info',
        extra: {
          code: err.code,
          message: err.message,
        },
      });
    },
    [clearOrderWatchdog, setLoading, setCheckoutDetails],
  );

  /**
   * A receipt that failed validation, including our own validator answering
   * `ok: false`. The only channel for it: it never reaches `store.error()`.
   */
  const onTransactionUnverified = useCallback(
    ({ payload }: CdvPurchase.UnverifiedReceipt) => {
      clearOrderWatchdog();
      orderInFlightRef.current = false;
      setLoading(false);

      Sentry.captureMessage('iap-transaction-unverified', {
        level: 'warning',
        extra: { code: payload?.code, message: payload?.message },
      });

      if (!failureHandledRef.current) {
        failureHandledRef.current = true;
        enqueueSnackbar(t('membershipCheckout.unknownError'), {
          variant: 'error',
        });
      }
    },
    [clearOrderWatchdog, setLoading, t],
  );

  const handleValidatorError = useCallback(
    (
      callback: CdvPurchase.Callback<CdvPurchase.Validator.Response.Payload>,
      err: Error & { response?: { status: number } },
    ) => {
      setLoading(false);
      failureHandledRef.current = true;

      // This fires after Apple's payment sheet, so possibly after a charge.
      if (parseRecurlyErrorToTransKey(err) === 'common.alreadyAMember') {
        enqueueSnackbar(t('membershipCheckout.membershipExistsForEmail'), {
          variant: 'error',
          autoHideDuration: 15000,
        });
        Sentry.captureException(err);
      } else {
        recurlyOnErrorHandler(err, t);
      }

      callback({
        ok: false,
        status: err?.response?.status || HttpStatusCode.BadRequest,
      });
    },
    [setLoading, t],
  );

  const preCreateIAP = (): void => {
    // We pre process the iap purchase before the order sends off to apple on the button click
    //   to ensure we don't get a race condition where recurly creates an account prior to our iap_process endpoint
    const formValues = getValues();
    // we are explicitly not handling the await/failure states on this POST because it is not critical to the flow
    //   that it succeeds. This covers a <5% race case condition and if its failing first, we shouldn't block the
    //   rest of checkout.
    preProcessIapPurchase({
      externalAccountCode: checkoutDetails.externalAccountCode,
      email: formValues.email,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      optInToUpdates: formValues.optInToUpdates,
    });
  };

  const createIapPurchase = useCallback(
    (
      receipt: CdvPurchase.Validator.Request.Body,
      callback: CdvPurchase.Callback<CdvPurchase.Validator.Response.Payload>,
    ) => {
      const formValues = getValues();

      if (!formValues.email || !formValues.firstName || !formValues.lastName) {
        // Handle edge cases: Unprocessed or duplicate transactions with reset form/IAP state
        //
        // Scenarios:
        // 1. An "approved" transaction hasn't been processed
        // 2. A transaction is approved multiple times after form reset
        // 3. The IAP (In-App Purchase) state has been reset
        //
        // Potential causes:
        // - Asynchronous validator causing duplicate approvals due to race conditions
        // - Legacy implementation not finalizing transactions correctly
        // - App uninstalls during incomplete transactions
        // - Network issues or app crashes interrupting the purchase flow
        //
        // Purpose:
        // - Prevent duplicate transaction processing
        // - Ensure consistent IAP state across error scenarios
        // - Handle rare edge cases in the purchase flow
        //
        // Note: Scenario 2 likely occurs due to the async behavior of the
        // createIapPurchase validator function.
        callback({ ok: false, status: HttpStatusCode.BadRequest });
        return;
      }

      const transaction =
        receipt.transaction as CdvPurchase.Validator.Response.NativeTransaction;

      if (isMembershipUpgrade) {
        upgradeMembership(
          {
            verificationToken: checkoutDetails.verificationToken,
            userId: user?.id,
            upgradePlanCode: selectedProductId,
            fromIap: true,
          },
          {
            onSuccess: () => {
              const price = getPriceFromStore(selectedProductId);
              logPurchaseEvent({
                productId: selectedProductId,
                price,
                transactionType: 'membership',
                paymentMethod: 'iap',
                isUpgrade: true,
              });
              handleValidatorSuccess(callback, transaction, selectedProductId);
            },
            onError: (err) => handleValidatorError(callback, err),
          },
        );

        return;
      }

      processIapPurchase(
        {
          clientToken: checkoutDetails.clientToken,
          externalAccountCode: checkoutDetails.externalAccountCode,
          email: formValues.email,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          optInToUpdates: formValues.optInToUpdates,
          isRecurring: true,
          planCode: selectedProductId,
          isReconciliation: false,
        },
        {
          onSuccess: () => {
            const price = getPriceFromStore(selectedProductId);
            logPurchaseEvent({
              productId: selectedProductId,
              price,
              transactionType: 'membership',
              paymentMethod: 'iap',
              isUpgrade: false,
            });
            handleValidatorSuccess(callback, transaction, selectedProductId);
          },
          onError: (err) => handleValidatorError(callback, err),
        },
      );
    },
    [
      checkoutDetails.clientToken,
      checkoutDetails.externalAccountCode,
      checkoutDetails.verificationToken,
      getValues,
      handleValidatorError,
      isMembershipUpgrade,
      processIapPurchase,
      selectedProductId,
      upgradeMembership,
      user?.id,
      logPurchaseEvent,
    ],
  );

  useEffect(() => {
    CdvPurchase.store
      .when()
      .approved(onTransactionApproved)
      .verified(onTransactionVerified)
      .unverified(onTransactionUnverified)
      .finished(onTransactionFinished);

    CdvPurchase.store.error(onStoreError);

    // Set ios app account token
    CdvPurchase.store.applicationUsername = checkoutDetails.externalAccountCode;

    // Create iap purchase on api before finishing transaction
    CdvPurchase.store.validator = createIapPurchase;

    return () => {
      // Unregister handlers so re-renders don't accumulate duplicates
      CdvPurchase.store.off(onTransactionApproved);
      CdvPurchase.store.off(onTransactionVerified);
      CdvPurchase.store.off(onTransactionUnverified);
      CdvPurchase.store.off(onTransactionFinished);
      CdvPurchase.store.off(onStoreError);
      clearOrderWatchdog();
    };
  }, [
    checkoutDetails.externalAccountCode,
    createIapPurchase,
    onStoreError,
    onTransactionApproved,
    onTransactionFinished,
    onTransactionUnverified,
    onTransactionVerified,
    clearOrderWatchdog,
  ]);

  const handlePurchase = async (values: FormValues): Promise<void> => {
    const product = CdvPurchase.store.get(
      selectedProductId,
      CdvPurchase.Platform.APPLE_APPSTORE,
    );
    const offer = product?.getOffer();

    if (!isMembershipUpgrade) {
      checkoutStartedAtRef.current = Date.now();

      // Only restore when a replay could change the outcome. A re-purchase is
      // never blocked, and an unfinished receipt that would stop `order()` is
      // already in the queue for the `canPurchase` check below to clear.
      if (!hasOwnMembershipHistory) {
        // A correctly finished purchase leaves an empty queue, so without a
        // restore an Apple ID that already owns a subscription looks new. A
        // replay proves it bought one, and this account has no history to
        // explain it, so the subscription belongs to another account.
        setLoading(true);

        // Gate the listener for the restore only. Once the set is in hand the
        // verdict is made, and a replay landing later is better finished by the
        // listener than ignored.
        isRestoringIapRef.current = true;
        const [, ownedTransactions] = await attemptAsync(
          restoreAndFindApprovedTransactions,
        );
        isRestoringIapRef.current = false;

        if (ownedTransactions?.length) {
          Sentry.captureMessage('iap-pre-order-collision', {
            level: 'info',
            tags: {
              productId: selectedProductId,
              ownedProductIds:
                ownedTransactions
                  .flatMap((txn) => txn.products?.map((p) => p.id) ?? [])
                  .join(',') || 'none',
              isLoggedIn,
            },
          });

          await Promise.all(ownedTransactions.map(finishStaleTransaction));
          await showCollisionDialog('approved-replay');
          return;
        }
      }
    }

    // Since plugin 13.16.1 `canPurchase` is false while a matching transaction
    // is unfinished, and ordering anyway is accepted then answered with nothing.
    // `owned` separates a dirty queue, which we can clear, from an entitlement.
    // Only an explicit `false` blocks: an absent value is not a refusal.
    //
    // Hard to reach on purpose, and not the main collision check: it needs a
    // subscription the restore above could not see, because a re-purchase
    // skipped the restore or StoreKit did not replay it. Force it in a dev
    // build to test the dialog.
    if (offer?.canPurchase === false) {
      const stale = CdvPurchase.store.localTransactions.filter(
        isApprovedWatchDutyTransaction,
      );

      if (stale.length) {
        // A re-purchase skips the restore above, so raise it here.
        setLoading(true);
        await Promise.all(stale.map(finishStaleTransaction));
        await reportIfTransactionsRemain('iap-cannot-purchase');
      }

      // `canPurchase` is derived from the queue, so re-read it once cleared.
      const canPurchaseNow = Boolean(
        CdvPurchase.store
          .get(selectedProductId, CdvPurchase.Platform.APPLE_APPSTORE)
          ?.getOffer()?.canPurchase,
      );

      if (!canPurchaseNow) {
        // Ordering now would be accepted and never answered, leaving the user
        // on a loader until the order timer gives up. Say so now instead.
        Sentry.captureMessage('iap-offer-not-purchasable', {
          level: 'info',
          tags: {
            productId: selectedProductId,
            productOwned: Boolean(product?.owned),
          },
        });

        await showCollisionDialog('on-file');
        return;
      }
    }

    if (offer) {
      setCheckoutDetails({
        ...values,
        checkoutStartedAt: checkoutStartedAtRef.current,
        purchaseType: isMembershipUpgrade ? 'upgrade' : 'new',
        accountUserId: user?.id ?? null,
      });
      if (!isMembershipUpgrade) {
        preCreateIAP();
      }

      failureHandledRef.current = false;
      orderInFlightRef.current = true;
      setLoading(true);

      // Started before the await because `order()` itself may never resolve.
      // Only this order's own outcome stops it; an unrelated replay or renewal
      // lets it keep running.
      clearOrderWatchdog();
      orderWatchdogRef.current = setTimeout(() => {
        orderWatchdogRef.current = undefined;

        // The attempt is over. A real transaction arriving later is provisioned
        // by `useReconcileInterruptedIap` at the next launch, which beats a page
        // that would spend any future transaction on the selected plan.
        orderInFlightRef.current = false;

        // Apple answers an already-owned plan with
        // `PaymentTransactionStateRestored` and the plugin can suppress the
        // repeated `approved`, so read the queue rather than await a callback.
        const ownedTransaction = CdvPurchase.store.localTransactions.find(
          (transaction) =>
            isApprovedWatchDutyTransaction(transaction) &&
            transaction.products?.some(
              (transactionProduct) =>
                transactionProduct.id === selectedProductId,
            ),
        );

        Sentry.captureMessage('iap-order-no-response', {
          level: 'warning',
          tags: {
            productId: selectedProductId,
            alreadyOwned: Boolean(ownedTransaction),
          },
        });

        if (ownedTransaction) {
          finishStaleTransaction(ownedTransaction)
            .then(() => showCollisionDialog('on-file'))
            .catch((err) => {
              Sentry.captureException(err, {
                extra: { context: 'iap-order-watchdog' },
              });
            });
          return;
        }

        setLoading(false);

        if (!failureHandledRef.current) {
          failureHandledRef.current = true;
          enqueueSnackbar(t('membershipCheckout.unknownError'), {
            variant: 'error',
          });
        }
      }, ORDER_RESPONSE_TIMEOUT_MS);

      // `order()` resolves with an `IError` rather than throwing, and an unready
      // adapter is returned here and reported nowhere else.

      const orderError = await offer.order();

      if (orderError && !failureHandledRef.current) {
        onStoreError(orderError);
      }
    } else {
      // The restore above may have raised the loader; don't leave it up when
      // there is nothing to order.
      setLoading(false);
    }
  };

  const { emailWarningText, emailFieldSxProps } = useEmailValidation({
    fieldKey: 'email',
    errors,
    watch,
  });

  const formattedPrice = useMemo(
    () => getFormattedPriceFromStore(selectedProductId),
    [selectedProductId],
  );

  if (loading) {
    return <Loader disableBackground />;
  }

  return (
    <FormWithoutEnter
      className={classes.container}
      noValidate
      onSubmit={handleSubmit(handlePurchase)}
    >
      <Stack spacing={2} sx={{ flex: 1, marginBottom: 3 }}>
        <Typography variant="h2">
          <b>{t('membershipCheckout.sections.account')}</b>
        </Typography>

        {showMembershipEndedNotice && (
          <Alert icon={false} severity="info" variant="standard">
            <Typography variant="body2" sx={{ marginBottom: '8px' }}>
              {t('membershipCheckout.membershipEnded.message', {
                date: formattedActiveUntil,
              })}
            </Typography>

            <Typography variant="body2">
              <Trans i18nKey="membershipCheckout.support">
                Need help? Contact us at
                <MuiLink
                  href="mailto:support@watchduty.org"
                  target="_blank"
                  rel="noreferrer"
                  color="inherit"
                >
                  support@watchduty.org
                </MuiLink>
                .
              </Trans>
            </Typography>
          </Alert>
        )}

        {errors.email?.type === 'managed' && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography sx={{ marginBottom: '16px' }}>
              {errors.email.message}
            </Typography>

            <Typography>{t('organization.contactYourAdmin')}</Typography>
          </Alert>
        )}

        {errors.email?.type === 'registered' && (
          <Alert icon={false} severity="error" variant="standard">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                marginBottom: '16px',
              }}
            >
              {errors.email.message}
            </Typography>

            <MuiLink
              to="/login"
              component={Link}
              color="inherit"
              underline="always"
              variant="body2"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('membershipCheckout.login')}
            </MuiLink>
          </Alert>
        )}

        {(!user?.firstName || !user?.lastName) && (
          <Grid container spacing={2} sx={{ width: '100%' }}>
            {!user?.firstName && (
              <Grid size={6}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field, fieldState }): JSX.Element => {
                    const { ref, ...muiFieldProps } = field;
                    return (
                      <TextField
                        id="field-control-firstName"
                        label={t('membershipCheckout.inputs.firstName.label')}
                        fullWidth
                        {...muiFieldProps}
                        inputRef={ref}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        required
                      />
                    );
                  }}
                />
              </Grid>
            )}

            {!user?.lastName && (
              <Grid size={6}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field, fieldState }): JSX.Element => {
                    const { ref, ...muiFieldProps } = field;
                    return (
                      <TextField
                        id="field-control-lastName"
                        label={t('membershipCheckout.inputs.lastName.label')}
                        fullWidth
                        {...muiFieldProps}
                        inputRef={ref}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                        required
                      />
                    );
                  }}
                />
              </Grid>
            )}
          </Grid>
        )}

        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-email"
                label={t('membershipCheckout.inputs.email.label')}
                fullWidth
                type="email"
                {...muiFieldProps}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={
                  (fieldState.error?.type !== 'registered' &&
                  fieldState.error?.type !== 'managed'
                    ? fieldState.error?.message
                    : undefined) || emailWarningText
                }
                required
                sx={emailFieldSxProps}
                disabled={Boolean(user?.email)}
                color={emailWarningText ? 'warning' : undefined}
                focused={Boolean(emailWarningText)}
              />
            );
          }}
        />

        {!user && (
          <Controller
            name="optInToUpdates"
            control={control}
            render={({ field }): JSX.Element => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      className={classes.checkbox}
                    />
                  }
                  label={t('membershipCheckout.inputs.optInToUpdates.label')}
                />
              );
            }}
          />
        )}

        {isMembershipUpgrade && (
          <Typography variant="body2" sx={{ marginTop: 1 }}>
            {t('membershipCheckout.upgrade')}
          </Typography>
        )}
      </Stack>

      <Stack spacing={2}>
        <CheckoutPriceSummary
          title={
            isMembershipUpgrade
              ? t('membershipCheckout.total')
              : t(MEMBERSHIP_TITLE_TRANS_KEYS[plan])
          }
          price={formattedPrice}
          description={t(MEMBERSHIP_DESCRIPTION_TRANS_KEYS[plan], {
            price: formattedPrice,
          })}
          descriptionVisible={!isKeyboardOpen}
        />

        <Button
          size="large"
          fullWidth
          type="submit"
          disabled={!isValid || isSubmitting}
          startIcon={<AppleIcon width={24} height={24} />}
          sx={{
            '&:enabled': {
              '& svg path': {
                fill: 'black',
              },
            },
          }}
        >
          {t(
            `membershipCheckout.button.${isMembershipUpgrade ? 'upgrade' : 'subscribe'}`,
          )}
        </Button>
      </Stack>
    </FormWithoutEnter>
  );
};
